import "../styles/chat.css";
import { useEffect, useState } from "react";
import Section from "../../../components/base/Section";
import Layout from "../../../components/layouts/Layout";
import { addOrUpdateChat, closeChat, getUser, hasRole, openNewChatModal, selectChat, selectChatById, setPage, setToolbarActions, useEnsureChats, useGlobal, useScreen, useSelectedChat } from "../../../core/Global";
import ChatContactsBox from "../components/ChatContactsBox";
import ChatMobileList from "../components/ChatMobileList";
import ArchivedChatsDrawer from "../components/ArchivedChatsDrawer";
import { CHAT_BLOCK_CHANGED_EVENT } from "../components/ChatContactContextMenu";
import ChatBox from "../components/ChatBox";
import ChatContactItem from "../models/ChatContactItem";
import ChatUnselectedView from "../components/ChatUnselectedView";
import { useNavigate, useParams } from "react-router-dom";
import ChatTechBox from "../components/ChatTechBox";
import Modal from "../../../components/base/Modal";
import ChatMessage from "../models/ChatMessage";
import { CompactJob, CompactUser } from "../../../core/CompactModels";
import AssignMessageToJob from "../components/AssignMessageToJob";
import Icon from "../../../components/base/Icon";
import { Drawer } from "../../../components/drawers/Drawer";
import FlexRow from "../../../components/base/FlexRow";
import { usePWAStatus } from "../../../hooks/usePWAStatus";


export default function ChatMainView() {

    const { isStandalone } = usePWAStatus()
    const user = getUser()
    const [globalState] = useGlobal()
    const screen = useScreen()
    const { selectedId } = useParams()
    const navigate = useNavigate()

    const selectedChat = useSelectedChat()

    useEnsureChats()

    const [refreshContacts, refreshContactsSet] = useState<boolean>(false)
    const [resetContacts, resetContactsSet] = useState<boolean>(false)
    const [readedChatId, readedChatIdSet] = useState<string>();
    const [searchingChat, searchingChatSet] = useState<ChatContactItem>();

    const [assignToJob, assignToJobSet] = useState<boolean>(false);
    const [assignToJobMessage, assignToJobMessageSet] = useState<ChatMessage>();

    const [archivedOpen, archivedOpenSet] = useState<boolean>(false);



    useEffect(() => {
        if (readedChatId) {
            setTimeout(() => {
                readedChatIdSet(undefined);
            }, 1000);
        }
    }, [readedChatId])

    useEffect(() => {
        if (refreshContacts) {
            setTimeout(() => {
                refreshContactsSet(false);
            }, 1000);
        }
    }, [refreshContacts])

    useEffect(() => {
        if (resetContacts) {
            setTimeout(() => {
                resetContactsSet(false);
            }, 1000);
        }
    }, [resetContacts])

    // An unblocked chat goes straight back to the live chat list, so the sidebar leaves the
    // archived / blocked view and shows that list too (the mobile drawer closes itself).
    useEffect(() => {
        const onChange = (e: Event) => {
            if ((e as CustomEvent).detail?.blocked === false) resetContactsSet(true);
        };
        window.addEventListener(CHAT_BLOCK_CHANGED_EVENT, onChange);
        return () => window.removeEventListener(CHAT_BLOCK_CHANGED_EVENT, onChange);
    }, [])

    const ActionButtons = () => (
        <>
            <button className="btn btn-primary btn-sm" onClick={e => {
                openNewChatModal()
            }}>
                <Icon icon="edit" />
                Start New Chat
            </button>
        </>
    )


    useEffect(() => {
        if (selectedChat && selectedChat.chatId != selectedId) {
            navigate(`/chat/${selectedChat.chatId}`, {
                replace: true
            })
        }
    }, [selectedChat?.chatId])

    useEffect(() => {
        if (selectedChat && selectedId == selectedChat.chatId) return;
        if (selectedId && globalState.chatlist) selectChatById(selectedId);
        else closeChat()
    }, [selectedId, globalState.chatlist])


    useEffect(() => {
        setPage("Chat");
    }, [globalState.chatlist])


    return (
        <Layout wide noToolbar paddingBottom={0} paddingTop={0}>
            <Section fluid fit>
                {
                    (screen.isMobile || isStandalone) ?
                        <div>
                            {
                                globalState.techChat ?
                                    <ChatBox key={globalState.techChat.chatId} chat={globalState.techChat} chatId={globalState.techChat.chatId}
                                        onRead={() => readedChatIdSet(globalState.techChat?.chatId)}
                                        onPin={() => refreshContactsSet(true)}
                                        onUnpin={() => refreshContactsSet(true)}
                                        onSearch={chat => searchingChatSet(chat)}
                                        onMessageSending={msg => resetContactsSet(true)}
                                        onAssignMessageToJob={msg => {
                                            assignToJobSet(true);
                                            assignToJobMessageSet(msg);
                                        }} />

                                    : selectedChat ?
                                        <Drawer id="drawer-open-chat"
                                            title={selectedChat.title}
                                            noPadding noScroll
                                            onClose={() => navigate("/chat", { replace: true })}>
                                            <ChatBox key={selectedChat.chatId} chat={selectedChat} chatId={selectedChat.chatId}
                                                onRead={() => readedChatIdSet(selectedChat.chatId)} inDrawer
                                                onPin={() => refreshContactsSet(true)}
                                                onUnpin={() => refreshContactsSet(true)}
                                                onSearch={chat => searchingChatSet(chat)}
                                                onMessageSending={msg => resetContactsSet(true)}
                                                onMessageSent={chat => addOrUpdateChat(chat)}
                                                onAssignMessageToJob={msg => {
                                                    assignToJobSet(true);
                                                    assignToJobMessageSet(msg);
                                                }} />
                                        </Drawer>
                                        : !user?.isTech ?
                                            (searchingChat ?
                                                <ChatContactsBox
                                                    reset={resetContacts}
                                                    readedChatId={readedChatId}
                                                    searchingChat={searchingChat}
                                                    onNewChat={() => openNewChatModal()}
                                                    onSearchingChatCleared={() => {
                                                        searchingChatSet(undefined)
                                                    }} />
                                                :
                                                <ChatMobileList
                                                    readedChatId={readedChatId}
                                                    archivedOpen={archivedOpen}
                                                    onNewChat={() => openNewChatModal()}
                                                    onOpenArchived={() => archivedOpenSet(true)} />)
                                            : <></>

                            }
                        </div>
                        :
                        <FlexRow>

                            <div className="flex-column flex-lg-row-auto w-100 w-lg-250px w-xl-300px border-end">
                                {
                                    user?.isTech ? <></>
                                        :
                                        <ChatContactsBox
                                            reset={resetContacts}
                                            readedChatId={readedChatId}
                                            searchingChat={searchingChat}
                                            onNewChat={() => openNewChatModal()}
                                            onSearchingChatCleared={() => {
                                                searchingChatSet(undefined);
                                            }} />
                                }
                            </div>

                            <div className="flex-lg-row-fluid">
                                {
                                    selectedChat ?
                                        <FlexRow>

                                            <div className={selectedChat.isSystem ? "col-12 w-100" : "col-md-12 col-lg-8"}>
                                                <ChatBox key={selectedChat.chatId} chat={selectedChat} chatId={selectedChat.chatId}
                                                    onRead={() => readedChatIdSet(selectedChat.chatId)}
                                                    onPin={() => refreshContactsSet(true)}
                                                    onUnpin={() => refreshContactsSet(true)}
                                                    onSearch={chat => searchingChatSet(chat)}
                                                    onMessageSending={msg => resetContactsSet(true)}
                                                    onMessageSent={chat => addOrUpdateChat(chat)}
                                                    onAssignMessageToJob={msg => {
                                                        assignToJobSet(true);
                                                        assignToJobMessageSet(msg);
                                                    }} />
                                            </div>
                                            {
                                                !selectedChat.isSystem &&
                                                <div className="d-md-none d-lg-block col-lg-4">
                                                    <ChatTechBox chat={selectedChat} />
                                                </div>
                                            }

                                        </FlexRow>
                                        :
                                        <ChatUnselectedView />
                                }
                            </div>

                        </FlexRow>
                }

            </Section>

            <Modal id="modal-pinned-chats-exceeed"
                title="Alert"
                buttonText="Okay"
                buttonColor="secondary"
                onConfirm={callback => callback()}
                preventDiscard>
                <p>
                    You can't pin more than 5 chats!
                </p>
            </Modal>


            {
                assignToJob &&
                <AssignMessageToJob message={assignToJobMessage}
                    onClose={() => {
                        assignToJobSet(false);
                        assignToJobMessageSet(undefined);
                    }} />
            }

            {
                archivedOpen &&
                <ArchivedChatsDrawer
                    readedChatId={readedChatId}
                    onClose={() => archivedOpenSet(false)} />
            }




        </Layout>
    )
}